import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useLanguage } from '@/contexts/LanguageContext';
import { Database, Search, Brain, Shield, CheckCircle, ArrowRight, TrendingUp, Zap, Users, Target } from 'lucide-react';
import { Link } from 'react-router-dom';

const RAGSolutions = () => {
  const { t } = useLanguage();
  
  useSEO({
    title: "Enterprise RAG Solutions | Retrieval-Augmented Generation",
    description: "Implement enterprise RAG systems that reduce AI hallucinations by 90%. Custom RAG development, $40B market leader. Get free RAG assessment.",
    keywords: "enterprise RAG implementation, retrieval augmented generation, RAG vs traditional chatbots, custom RAG development, AI hallucination reduction, enterprise AI chatbots, RAG consulting",
    ogTitle: "Enterprise RAG Solutions | Reduce AI Hallucinations by 90%",
    ogDescription: "Leading RAG implementation services. 90% hallucination reduction, enterprise-grade security, custom RAG systems. Free consultation.",
    canonical: "https://quantumbox.ai/rag-solutions"
  });

  const ragAdvantages = [
    {
      icon: Brain,
      title: "90% Hallucination Reduction",
      description: "RAG grounds AI responses in your actual data, eliminating false information",
      metric: "99.1% accuracy rate",
      comparison: "vs 60% with traditional LLMs"
    },
    {
      icon: Database,
      title: "Real-Time Data Access",
      description: "Access live business data, documents, and databases for current information",
      metric: "Instant updates",
      comparison: "vs static training data"
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Your data stays secure within your infrastructure, never sent to external APIs",
      metric: "100% data control",
      comparison: "vs cloud-dependent models"
    },
    {
      icon: Search,
      title: "Contextual Intelligence",
      description: "Retrieve relevant context from vast knowledge bases for precise responses",
      metric: "10x more relevant",
      comparison: "vs generic responses"
    }
  ];

  const marketStats = [
    {
      metric: "$1.96B",
      description: "RAG Market Size 2025",
      growth: "+35.31% CAGR"
    },
    {
      metric: "$40.34B",
      description: "Projected by 2035",
      growth: "2000% growth"
    },
    {
      metric: "90%",
      description: "Accuracy Improvement",
      growth: "vs Traditional AI"
    }
  ];

  const useCases = [
    {
      industry: "Customer Support",
      title: "Intelligent Help Desk RAG",
      challenge: "Support agents spending hours searching through documentation",
      solution: "RAG system instantly retrieves relevant solutions from knowledge base",
      results: ["65% faster ticket resolution", "90% first-contact resolution", "40% reduction in escalations"],
      implementation: "2-week setup"
    },
    {
      industry: "Legal Services",
      title: "Legal Document RAG",
      challenge: "Lawyers manually reviewing thousands of case documents",
      solution: "RAG extracts relevant precedents and clauses from legal databases",
      results: ["80% faster document review", "95% accuracy in precedent finding", "50% cost reduction"],
      implementation: "3-week setup"
    },
    {
      industry: "Healthcare",
      title: "Medical Knowledge RAG",
      challenge: "Doctors need instant access to latest medical research and guidelines",
      solution: "RAG retrieves current medical protocols and research findings",
      results: ["Instant protocol access", "99.5% medical accuracy", "30% faster diagnoses"],
      implementation: "4-week setup"
    }
  ];

  const implementation = [
    {
      step: 1,
      title: "Data Assessment & Architecture",
      description: "Analyze your data sources and design optimal RAG architecture",
      deliverables: ["Data audit report", "RAG system blueprint", "Security framework"]
    },
    {
      step: 2,
      title: "Vector Database Setup",
      description: "Configure high-performance vector storage and retrieval systems",
      deliverables: ["Vector database", "Embedding models", "Search optimization"]
    },
    {
      step: 3,
      title: "RAG Pipeline Development",
      description: "Build custom retrieval and generation pipelines for your use case",
      deliverables: ["Custom RAG model", "API endpoints", "Integration layer"]
    },
    {
      step: 4,
      title: "Testing & Optimization",
      description: "Comprehensive testing, accuracy validation, and performance tuning",
      deliverables: ["Accuracy reports", "Performance metrics", "Optimization plan"]
    },
    {
      step: 5,
      title: "Deployment & Monitoring",
      description: "Production deployment with continuous monitoring and improvement",
      deliverables: ["Live RAG system", "Monitoring dashboard", "Maintenance plan"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-5xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-primary to-secondary text-white border-0">
              $40B Market by 2035 • 35.31% CAGR
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Enterprise RAG Solutions
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
              Implement Retrieval-Augmented Generation systems that reduce AI hallucinations by 90% 
              and provide accurate, contextual responses from your enterprise data.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button size="lg" asChild className="bg-gradient-to-r from-primary to-secondary hover:scale-105 transition-transform">
                <Link to="/assessment">Get Free RAG Assessment</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/contact">Schedule RAG Demo</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Market Stats */}
      <section className="py-16 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {marketStats.map((stat, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <div className="text-4xl font-bold text-primary mb-2">{stat.metric}</div>
                  <div className="text-lg font-medium mb-1">{stat.description}</div>
                  <Badge variant="secondary" className="text-green-600">{stat.growth}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* RAG vs Traditional Comparison */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">RAG vs Traditional Chatbots: Why Your Business Needs to Switch</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Stop losing customers to inaccurate AI responses. RAG technology ensures your AI always has the right answer.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {ragAdvantages.map((advantage, index) => (
              <Card key={index} className="relative overflow-hidden group hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20">
                      <advantage.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl">{advantage.title}</CardTitle>
                      <div className="flex gap-2 mt-1">
                        <Badge className="bg-green-100 text-green-800">{advantage.metric}</Badge>
                        <Badge variant="outline" className="text-red-600">{advantage.comparison}</Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{advantage.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Enterprise RAG Success Stories</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Real implementations delivering measurable business impact across industries
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="relative overflow-hidden">
                <CardHeader>
                  <Badge className="w-fit mb-2">{useCase.industry}</Badge>
                  <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="font-medium text-red-600 mb-1">Challenge:</div>
                      <div className="text-muted-foreground">{useCase.challenge}</div>
                    </div>
                    <div>
                      <div className="font-medium text-blue-600 mb-1">RAG Solution:</div>
                      <div className="text-muted-foreground">{useCase.solution}</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <div className="font-medium text-green-600 mb-2">Results:</div>
                    {useCase.results.map((result, resultIndex) => (
                      <div key={resultIndex} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{result}</span>
                      </div>
                    ))}
                  </div>
                  <Badge variant="secondary" className="w-fit">
                    Implementation: {useCase.implementation}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Complete RAG Implementation Guide</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our proven 5-step process for deploying enterprise-grade RAG systems
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            {implementation.map((phase, index) => (
              <Card key={index} className="mb-6 last:mb-0 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold">
                      {phase.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{phase.title}</h3>
                      <p className="text-muted-foreground mb-4">{phase.description}</p>
                      <div>
                        <div className="font-medium mb-2">Deliverables:</div>
                        <div className="flex flex-wrap gap-2">
                          {phase.deliverables.map((deliverable, deliverableIndex) => (
                            <Badge key={deliverableIndex} variant="outline">{deliverable}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Eliminate AI Hallucinations?</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Join the $40B RAG revolution. Get your free assessment and discover how RAG can transform 
            your business intelligence and customer interactions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-secondary hover:scale-105 transition-transform">
              <Link to="/assessment">
                Get Free RAG Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">Schedule RAG Demo</Link>
            </Button>
          </div>
          <div className="mt-8 text-sm text-muted-foreground">
            ✓ Free 30-minute consultation ✓ Custom RAG roadmap ✓ ROI calculator
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RAGSolutions;